import React, { useEffect, useState } from "react";

import { Button, Container } from "react-bootstrap";

import { Participant, ParticipantTypes, useSearchParticipantsQuery } from "@api/graphql";
import { ParticipantStatusBadge } from "@components/account_profile/ParticipantStatusBadge";
import { ProtectedComponent } from "@components/permission_control/ProtectedComponent";
import { useAuthenticationContext } from "@context/authentication";
import { Role } from "@roles";
import { getParticipantTypesForDisplay, isAMP, isAgent, isCMO, isCollaboration } from "@util/index";

import { InviteParticipantModal } from "./InviteParticipantModal";

import styles from "./ParticipantInfo.module.scss";

const PARTICIPANT_TYPES_TO_INVITE = [
  ParticipantTypes.AMP,
  ParticipantTypes.ART_CENTRE,
  ParticipantTypes.CMO,
  ParticipantTypes.AGENT,
];

const checkInviteButtonVisibility = (participantData?: Participant): boolean => {
  if (!participantData) {
    return false;
  }

  const hasInvitableType = participantData.participant_types?.some((type) =>
    PARTICIPANT_TYPES_TO_INVITE.includes(type),
  );

  const isArtistOrBeneficiary = participantData.participant_types?.some((type) =>
    [ParticipantTypes.ARTIST, ParticipantTypes.BENEFICIARY].includes(type),
  );

  const hasNoUuid = !participantData.uuid;

  return hasInvitableType || (isArtistOrBeneficiary && hasNoUuid);
};

const ParticipantInfo = ({ participantId, isAdminUser }: { participantId: number; isAdminUser: boolean }) => {
  const [participantData, setParticipantData] = useState<Participant | undefined>(undefined);
  const [showInviteModal, setShowInviteModal] = useState(false); // Add state for modal

  const { auth } = useAuthenticationContext();

  const { data } = useSearchParticipantsQuery({
    variables: {
      filter_by: [{ field: "participant_id", eq: participantId.toString() }],
    },
  });
  useEffect(() => {
    setParticipantData(data?.searchParticipants?.[0]);
  }, [data]);

  const isArtCentre = participantData?.participant_types?.includes(ParticipantTypes.ART_CENTRE);

  const participantTypesArray = participantData?.participant_types;

  const showCompanyName =
    participantTypesArray &&
    (isArtCentre ||
      isAMP(participantTypesArray) ||
      isCMO(participantTypesArray) ||
      isAgent(participantTypesArray) ||
      isCollaboration(participantTypesArray));
  const shouldDisplayInviteButton = checkInviteButtonVisibility(participantData);

  const participantTypes = getParticipantTypesForDisplay(participantData?.participant_types);

  const handleOpenModal = () => setShowInviteModal(true);
  const handleCloseModal = () => setShowInviteModal(false);

  return (
    <>
      {participantData && (
        <>
          <Container fluid>
            <div className={styles.participantInfo}>
              <div className={styles.header}>
                <div className="d-flex flex-column gap-3">
                  <div className={`${styles.beneficiary} b2`}>
                    {isAdminUser && participantTypes}
                    <br />
                    {isArtCentre
                      ? !showCompanyName && participantData.company_name
                      : participantTypes
                        ? !isAdminUser && participantTypes
                        : "Unknown Company"}
                  </div>
                  <h2 className={styles.name}>
                    {showCompanyName
                      ? participantData.company_name
                      : participantData.full_name || "Unknown Participant"}
                  </h2>
                </div>
                <div className={styles.statusContainer}>
                  <ProtectedComponent requiredGroups={[Role.VisualArtistAdmin, Role.MemberServices]} auth={auth}>
                    {shouldDisplayInviteButton && (
                      <Button
                        variant="primary"
                        onClick={handleOpenModal}
                        className={`ms-3 position-absolute ${styles.inviteUserButton}`}
                      >
                        Invite User
                      </Button>
                    )}
                  </ProtectedComponent>
                  <ProtectedComponent
                    requiredGroups={[Role.VisualArtistAdmin, Role.MemberServices, Role.Data]}
                    auth={auth}
                  >
                    <div className={`${styles.currentStatus} b4`}>Current status</div>
                    <ParticipantStatusBadge status={participantData.participant_status} />
                  </ProtectedComponent>
                </div>
              </div>
            </div>
          </Container>
          <InviteParticipantModal
            isOpen={showInviteModal}
            onClose={handleCloseModal}
            fullName={participantData.full_name}
            participantId={participantId}
          />
        </>
      )}
    </>
  );
};

export default ParticipantInfo;
